import {
  createEntityAdapter,
  createSelector,
  createSlice,
  current,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit"
import { PreferenceId, PriorityId, SubjectId } from "./types"
import { Brand, identity } from "ts-brand"
import {
  associatePriorityWithPreference,
  dataLoaded,
  dissociatePriorityWithPreference,
  removePreference,
} from "./actions"
import { queryOverlaps } from "@/lib/interval-overlaps"

export type EventId = Brand<string, "EventId">
export const EventId = ({ id }: { id: string }) => identity<EventId>(id)

type Event = {
  id: EventId
  priorityId: PriorityId
  preferenceId: PreferenceId
  subjectId: SubjectId
  eventTimezoneId: string
  active: boolean
  cycleLength: number
  events: Array<{
    startTime: string
    duration: number
    day: number
  }>
}

const eventsAdatper = createEntityAdapter<Event>()
const selectors = eventsAdatper.getSelectors()

type Conflicts = Record<EventId, Array<EventId>>
export const eventsSlice = createSlice({
  name: "events",
  initialState: eventsAdatper.getInitialState({
    cycleLength: 7,
    startInstant: "2025-01-06T00:00:00Z",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    conflicts: {} as Conflicts,

    highlightedPreferenceId: null as PreferenceId | null,
    highlightedPriorityId: null as PriorityId | null,

    snapshot: eventsAdatper.getInitialState({
      conflicts: {} as Conflicts,
    }),
  }),
  reducers: {
    preferenceHovered(state, action: PayloadAction<PreferenceId | null>) {
      state.highlightedPreferenceId = action.payload
    },

    priorityHovered(state, action: PayloadAction<PriorityId | null>) {
      state.highlightedPriorityId = action.payload
    },
  },

  selectors: {
    selectCalendar: createSelector([(state) => state], createCalendar),

    selectBaseCycleEvents: createSelector(
      [selectors.selectAll],
      createBaseCycleEvents,
    ),

    selectHighlightedPreferenceId: (state) => state.highlightedPreferenceId,

    selectHighlightedPriorityId: (state) => state.highlightedPriorityId,

    selectConflicts: (state) => state.conflicts,

    selectPreferencePriorityEvent: createSelector(
      [
        selectors.selectAll,
        (_, { priorityId }: { priorityId: PriorityId }) => priorityId,
        (_, { preferenceId }: { preferenceId: PreferenceId }) => preferenceId,
      ],
      (events, priorityId, preferenceId) =>
        events.find(
          (e) => e.priorityId === priorityId && e.preferenceId === preferenceId,
        ),
    ),

    selectSubjectEvents: createSelector(
      [
        selectors.selectAll,
        (_, { subjectId }: { subjectId: SubjectId }) => subjectId,
      ],
      (events, subjectId) => events.filter((e) => e.subjectId === subjectId),
    ),
  },

  extraReducers(builder) {
    builder.addCase(dataLoaded, (state, actions) => {
      eventsAdatper.removeAll(state)
      state.highlightedPreferenceId = null
      state.highlightedPriorityId = null
      state.cycleLength = 7

      actions.payload.forEach(({ id: subjectId, preferences }) => {
        preferences.forEach(
          ({ classDocument, priorities, id: preferenceId }) => {
            if (classDocument === null) {
              return
            }

            classDocument.timetablePriorities.forEach(
              ({ uuid: priorityId, sessions }) => {
                const id = EventId({
                  id: `${subjectId}:${preferenceId}:${priorityId}`,
                })

                if (classDocument.cycleLength > state.cycleLength) {
                  state.cycleLength = classDocument.cycleLength
                }

                eventsAdatper.addOne(state, {
                  id,
                  priorityId: PriorityId({ id: priorityId }),
                  preferenceId: PreferenceId({ id: preferenceId }),
                  subjectId: SubjectId({ id: subjectId }),
                  active: priorities.some((p) => p.priority === priorityId),
                  eventTimezoneId: classDocument.timezoneId,
                  cycleLength: classDocument.cycleLength,
                  events: sessions,
                })
              },
            )
          },
        )
      })

      setConflicts(state)
    })

    builder.addAsyncThunk(removePreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { preference } = action.meta.arg
        eventsAdatper.removeMany(
          state,
          selectors
            .selectAll(state)
            .filter((e) => e.preferenceId === preference.id)
            .map((e) => e.id),
        )

        // recalculate conflicts
        setConflicts(state)
      },
    })

    builder.addAsyncThunk(associatePriorityWithPreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { preference, priority } = action.meta.arg

        // set old events as inactive
        // priority can only be active for one preference
        setEventsInactive(state, priority.uuid)

        // set the now associated priority, preference events as active
        eventsAdatper.updateMany(
          state,
          selectors
            .selectAll(state)
            .filter(
              (e) =>
                e.preferenceId === preference.id &&
                e.priorityId === priority.uuid,
            )
            .map((e) => ({ id: e.id, changes: { active: true } })),
        )

        // recalcualte conflicts
        setConflicts(state)
      },
    })

    builder.addAsyncThunk(dissociatePriorityWithPreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { priority } = action.meta.arg
        setEventsInactive(state, priority.uuid)

        // recalcualte conflicts
        setConflicts(state)
      },
    })
  },
})

type EventsSlice = ReturnType<typeof eventsSlice.selectSlice>

function setEventsInactive(state: EventsSlice, priorityId: PriorityId) {
  eventsAdatper.updateMany(
    state,
    selectors
      .selectAll(state)
      .filter((e) => e.priorityId === priorityId)
      .map((e) => ({ id: e.id, changes: { active: false } })),
  )
}

function snapshotSlice(state: EventsSlice) {
  state.snapshot.entities = current(state.entities)
  state.snapshot.ids = current(state.ids)
  state.snapshot.conflicts = current(state.conflicts)
}

function restoreSnapshot(state: EventsSlice) {
  return {
    ...state,
    ...state.snapshot,
    snapshot: eventsAdatper.getInitialState({ conflicts: {} as Conflicts }),
  }
}

function createCalendar({
  timezone,
  cycleLength,
  startInstant,
}: {
  timezone: string
  cycleLength: number
  startInstant: string
}) {
  return {
    calendarTimezoneId: timezone,
    cycleLength: cycleLength,
    startTemporalInstant: Temporal.Instant.from(startInstant),
    startTemporalZonedDateTime:
      Temporal.Instant.from(startInstant).toZonedDateTimeISO("UTC"),
  }
}

function createBaseCycleEvents(events: Event[]) {
  return events.flatMap(({ events, active: _, ...rest }) =>
    events.map((session, index) => ({
      ...session,
      sessionIndex: index,
      ...rest,
    })),
  )
}

function setConflicts(state: EventsSlice) {
  const { startTemporalInstant } = createCalendar(state)
  const events = createBaseCycleEvents(selectors.selectAll(state))

  const conflicts: Conflicts = {}
  const overlaps = queryOverlaps(events, startTemporalInstant)

  overlaps.forEach(([id, events]) => {
    const { subjectId } = selectors.selectById(state, id as EventId)

    const eventIds = new Set(
      events.filter((e) => e.subjectId !== subjectId).map((e) => e.id),
    )
    if (eventIds.size === 0) return

    conflicts[id as EventId] = [...eventIds]
  })

  state.conflicts = conflicts
}

function gatherActiveConflicts(
  entities: EntityState<Event, EventId>["entities"],
  conflicts: Conflicts,
  map: Map<SubjectId, Array<[PriorityId, PreferenceId]>>,
  event: Event | undefined,
) {
  if (!event) return []

  const eventConflicts = conflicts[event.id]

  if (!eventConflicts) return []

  eventConflicts.forEach((eventId) => {
    const event = entities[eventId]

    if (!event || !event.active) {
      return
    }

    if (!map.has(event.subjectId)) {
      map.set(event.subjectId, [])
    }

    map.get(event.subjectId)!.push([event.priorityId, event.preferenceId])
  })
}

const {
  selectBaseCycleEvents,
  selectCalendar,
  selectConflicts,
  selectPreferencePriorityEvent,
  selectSubjectEvents,
  selectHighlightedPreferenceId,
  selectHighlightedPriorityId,
} = eventsSlice.selectors

const { selectEntities } = eventsAdatper.getSelectors(eventsSlice.selectSlice)
const selectPreferencePriorityConflicts = createSelector(
  [selectEntities, selectConflicts, selectPreferencePriorityEvent],
  (entities, conflicts, event) => {
    const subjectMap = new Map<SubjectId, Array<[PriorityId, PreferenceId]>>()
    gatherActiveConflicts(entities, conflicts, subjectMap, event)
    return Array.from(subjectMap)
  },
)

const selectSubjectConflicts = createSelector(
  [selectEntities, selectConflicts, selectSubjectEvents],
  (entities, conflicts, events) => {
    const subjectMap = new Map<SubjectId, Array<[PriorityId, PreferenceId]>>()

    events.forEach((event) => {
      if (!event.active) return
      gatherActiveConflicts(entities, conflicts, subjectMap, event)
    })

    return Array.from(subjectMap)
  },
)

export const eventsSelectors = {
  selectBaseCycleEvents,
  selectCalendar,
  selectPreferencePriorityConflicts,
  selectSubjectConflicts,
  selectHighlightedPreferenceId,
  selectHighlightedPriorityId,
}

const { preferenceHovered, priorityHovered } = eventsSlice.actions
export const eventsActions = { preferenceHovered, priorityHovered }
